import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { productAPI, categoryAPI } from '../../services/api';
import useScrollReveal from '../../hooks/useScrollReveal';
import useSeo from '../../hooks/useSeo';
import { useCart } from '../../context/CartContext';
import { Search, Package, ChevronLeft, ChevronRight, SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import { useFavorites } from '../../context/FavoritesContext';
import ProductCard from '../../components/ProductCard';

export default function Products() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [addingId, setAddingId] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const revealRef = useScrollReveal();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const { isFavorited, toggleFavorite } = useFavorites();

    const activeCatNameForSeo = searchParams.get('category') ? categories.find(c => String(c.id) === searchParams.get('category'))?.name : null;
    useSeo({
        title: activeCatNameForSeo
            ? `${activeCatNameForSeo} | Atölye Kirpi Tablo Mağazası`
            : 'Tablo Koleksiyonu | Kanvas, Çerçeveli, Dekoratif | Atölye Kirpi',
        description: activeCatNameForSeo
            ? `${activeCatNameForSeo} tablolarını keşfedin. Yüksek çözünürlük baskı, premium malzeme, ücretsiz kargo.`
            : 'Kanvas tablo, çerçeveli tablo, modern duvar tabloları. Yüksek kalite baskı, ücretsiz kargo, güvenli alışveriş.',
        keywords: `tablolar, ${activeCatNameForSeo || 'kanvas tablo, çerçeveli tablo, duvar tablosu'}, online alışveriş, Atölye Kirpi, tablo mağazası`,
        canonical: 'https://xn--atlyekirpi-fcb.com.tr/urunler'
    });

    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [category, setCategory] = useState(searchParams.get('category') || '');
    const [brand, setBrand] = useState(searchParams.get('brand') || '');
    const [sort, setSort] = useState(searchParams.get('sort') || 'newest');
    const [minPrice, setMinPrice] = useState(searchParams.get('minPrice') || '');
    const [maxPrice, setMaxPrice] = useState(searchParams.get('maxPrice') || '');
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
    const gridRef = useRef(null);

    useEffect(() => {
        const urlCategory = searchParams.get('category') || '';
        const urlSearch = searchParams.get('search') || '';
        const urlBrand = searchParams.get('brand') || '';
        const urlSort = searchParams.get('sort') || 'newest';
        const urlMinPrice = searchParams.get('minPrice') || '';
        const urlMaxPrice = searchParams.get('maxPrice') || '';
        const urlPage = parseInt(searchParams.get('page')) || 1;
        if (urlCategory !== category) setCategory(urlCategory);
        if (urlSearch !== search) setSearch(urlSearch);
        if (urlBrand !== brand) setBrand(urlBrand);
        if (urlSort !== sort) setSort(urlSort);
        if (urlMinPrice !== minPrice) setMinPrice(urlMinPrice);
        if (urlMaxPrice !== maxPrice) setMaxPrice(urlMaxPrice);
        if (urlPage !== page) setPage(urlPage);
    }, [searchParams]);

    useEffect(() => {
        categoryAPI.getAll({ active: 'true' }).then(res => setCategories(res.data || [])).catch(() => {});
    }, []);

    // Get subcategories for the selected parent category
    const parentCats = categories.filter(c => !c.parentId);
    const selectedParent = category ? categories.find(c => String(c.id) === String(category) && !c.parentId) : null;
    const selectedSubParent = category ? categories.find(c => String(c.id) === String(category) && c.parentId) : null;
    const activeParentId = selectedParent ? String(selectedParent.id) : selectedSubParent ? String(selectedSubParent.parentId) : null;
    const subcategories = activeParentId ? categories.filter(c => String(c.parentId) === activeParentId) : [];

    useEffect(() => {
        const catForBrands = category || '';
        if (catForBrands) {
            productAPI.getBrands({ category: catForBrands }).then(res => setBrands(res.data || [])).catch(() => setBrands([]));
        } else {
            setBrands([]);
            if (brand) setBrand('');
        }
    }, [category]);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const params = { page, limit: 12, sort, active: 'true' };
                if (search) params.search = search;
                if (category) params.category = category;
                if (brand) params.brand = brand;
                if (minPrice) params.minPrice = minPrice;
                if (maxPrice) params.maxPrice = maxPrice;
                const res = await productAPI.getAll(params);
                setProducts(res.data.products || []);
                setPagination(res.data.pagination || {});
            } catch (e) { console.error(e); }
            setLoading(false);
            requestAnimationFrame(() => {
                if (gridRef.current) {
                    gridRef.current.classList.add('visible');
                    gridRef.current.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale').forEach(el => el.classList.add('visible'));
                }
            });
        };
        fetchProducts();

        const params = {};
        if (search) params.search = search;
        if (category) params.category = category;
        if (brand) params.brand = brand;
        if (sort !== 'newest') params.sort = sort;
        if (minPrice) params.minPrice = minPrice;
        if (maxPrice) params.maxPrice = maxPrice;
        if (page > 1) params.page = page;
        setSearchParams(params);
    }, [search, category, brand, sort, minPrice, maxPrice, page]);

    const formatPrice = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);

    const handleAddToCart = async (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        if (product.hasVariants) {
            navigate(`/urun/${product.slug}`);
            return;
        }
        setAddingId(product.id);
        await addItem(product.id);
        setAddingId(null);
    };

    let searchTimer;
    const handleSearch = (value) => {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => { setSearch(value); setPage(1); }, 400);
    };

    let priceTimer;
    const handlePriceChange = (type, value) => {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(() => {
            if (type === 'min') setMinPrice(value);
            else setMaxPrice(value);
            setPage(1);
        }, 600);
    };

    const activeCatName = category ? categories.find(c => String(c.id) === String(category))?.name : null;
    const activeFilterCount = (category ? 1 : 0) + (brand ? 1 : 0) + (sort !== 'newest' ? 1 : 0) + (minPrice ? 1 : 0) + (maxPrice ? 1 : 0);
    const hasAnyFilter = category || brand || sort !== 'newest' || minPrice || maxPrice || search;

    const clearAllFilters = () => {
        setCategory(''); setBrand(''); setSort('newest'); setMinPrice(''); setMaxPrice(''); setSearch(''); setPage(1);
    };

    return (
        <div className="products-page" ref={revealRef}>
            {/* Hero Banner */}
            <div className="products-hero">
                <h1>{activeCatName || 'Koleksiyon'}</h1>
                <p>{pagination.total || 0} parça</p>
            </div>

            {/* Toolbar */}
            <div className="products-toolbar">
                <div className="products-toolbar-inner">
                    {/* Category Pills */}
                    <div className="products-cats">
                        <button
                            className={`products-cat-pill ${!category ? 'active' : ''}`}
                            onClick={() => { setCategory(''); setBrand(''); setPage(1); }}
                        >
                            Tümü
                        </button>
                        {parentCats.map(cat => (
                            <button
                                key={cat.id}
                                className={`products-cat-pill ${activeParentId === String(cat.id) ? 'active' : ''}`}
                                onClick={() => { setCategory(String(cat.id)); setBrand(''); setPage(1); }}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>

                    {/* Right: search + filter toggle */}
                    <div className="products-toolbar-right">
                        <div className="products-search">
                            <Search size={15} />
                            <input
                                placeholder="Ara..."
                                defaultValue={search}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                        </div>
                        <button className={`products-filter-btn ${filtersOpen ? 'active' : ''}`} onClick={() => setFiltersOpen(!filtersOpen)}>
                            <SlidersHorizontal size={15} />
                            <span>Filtre</span>
                            {activeFilterCount > 0 && <span className="products-filter-count">{activeFilterCount}</span>}
                            <ChevronDown size={13} className={`products-filter-chevron ${filtersOpen ? 'open' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Subcategory pills */}
                {subcategories.length > 0 && (
                    <div className="products-subcats">
                        <button
                            className={`products-subcat-pill ${String(category) === activeParentId ? 'active' : ''}`}
                            onClick={() => { setCategory(activeParentId); setPage(1); }}
                        >
                            Tümü
                        </button>
                        {subcategories.map(sub => (
                            <button
                                key={sub.id}
                                className={`products-subcat-pill ${String(category) === String(sub.id) ? 'active' : ''}`}
                                onClick={() => { setCategory(String(sub.id)); setPage(1); }}
                            >
                                {sub.name}
                            </button>
                        ))}
                    </div>
                )}

                {/* Expandable filter panel */}
                {filtersOpen && (
                    <div className="products-filters-panel">
                        <div className="products-filter-group">
                            <span className="products-filter-label">Sıralama</span>
                            <div className="products-filter-options">
                                {[
                                    { value: 'newest', label: 'En Yeni' },
                                    { value: 'price_asc', label: 'Fiyat ↑' },
                                    { value: 'price_desc', label: 'Fiyat ↓' },
                                    { value: 'name_asc', label: 'A → Z' },
                                    { value: 'name_desc', label: 'Z → A' },
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`products-filter-option ${sort === opt.value ? 'active' : ''}`}
                                        onClick={() => { setSort(opt.value); setPage(1); }}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="products-filter-group">
                            <span className="products-filter-label">Fiyat Aralığı</span>
                            <div className="products-price-range">
                                <div className="products-price-input">
                                    <span>₺</span>
                                    <input
                                        type="number"
                                        placeholder="Min"
                                        defaultValue={minPrice}
                                        onChange={(e) => handlePriceChange('min', e.target.value)}
                                    />
                                </div>
                                <span className="products-price-sep">—</span>
                                <div className="products-price-input">
                                    <span>₺</span>
                                    <input
                                        type="number"
                                        placeholder="Max"
                                        defaultValue={maxPrice}
                                        onChange={(e) => handlePriceChange('max', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {brands.length > 0 && (
                            <div className="products-filter-group">
                                <span className="products-filter-label">Marka</span>
                                <div className="products-filter-options">
                                    <button
                                        className={`products-filter-option ${!brand ? 'active' : ''}`}
                                        onClick={() => { setBrand(''); setPage(1); }}
                                    >
                                        Tümü
                                    </button>
                                    {brands.map(b => (
                                        <button
                                            key={b}
                                            className={`products-filter-option ${brand === b ? 'active' : ''}`}
                                            onClick={() => { setBrand(b); setPage(1); }}
                                        >
                                            {b}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {hasAnyFilter && (
                            <button className="products-filter-clear" onClick={clearAllFilters}>
                                <X size={13} /> Tüm Filtreleri Temizle
                            </button>
                        )}
                    </div>
                )}

                {/* Active filter tags */}
                {activeFilterCount > 0 && !filtersOpen && (
                    <div className="products-active-filters">
                        {category && (
                            <span className="products-active-tag">
                                {activeCatName}
                                <button onClick={() => { setCategory(''); setBrand(''); setPage(1); }}><X size={11} /></button>
                            </span>
                        )}
                        {brand && (
                            <span className="products-active-tag">
                                {brand}
                                <button onClick={() => { setBrand(''); setPage(1); }}><X size={11} /></button>
                            </span>
                        )}
                        {sort !== 'newest' && (
                            <span className="products-active-tag">
                                {{ price_asc: 'Fiyat ↑', price_desc: 'Fiyat ↓', name_asc: 'A→Z', name_desc: 'Z→A' }[sort]}
                                <button onClick={() => { setSort('newest'); setPage(1); }}><X size={11} /></button>
                            </span>
                        )}
                        {(minPrice || maxPrice) && (
                            <span className="products-active-tag">
                                {minPrice && maxPrice ? `₺${minPrice} – ₺${maxPrice}` : minPrice ? `₺${minPrice}+` : `₺${maxPrice}'e kadar`}
                                <button onClick={() => { setMinPrice(''); setMaxPrice(''); setPage(1); }}><X size={11} /></button>
                            </span>
                        )}
                        <button className="products-active-clear" onClick={clearAllFilters}>Temizle</button>
                    </div>
                )}
            </div>

            {/* Products Grid */}
            <div className="products-content">
                {loading ? (
                    <div className="loading"><div className="spinner"></div></div>
                ) : products.length === 0 ? (
                    <div className="empty-state">
                        <Package size={48} strokeWidth={1} />
                        <h3>Parça bulunamadı</h3>
                        <p>Farklı filtreler deneyin</p>
                    </div>
                ) : (
                    <>
                        <div ref={gridRef} className="products-grid reveal reveal-stagger">
                            {products.map(product => (
                                <ProductCard
                                    key={product.id}
                                    product={product}
                                    formatPrice={formatPrice}
                                    addingId={addingId}
                                    handleAddToCart={handleAddToCart}
                                    isFavorited={isFavorited}
                                    toggleFavorite={toggleFavorite}
                                    variantDiscountOnly
                                />
                            ))}
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="products-pagination">
                                <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                                    <ChevronLeft size={16} />
                                </button>
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                                    <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                                ))}
                                <button disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
